/**
 * BESTIX v5.0 — Value Proposition
 * Trust-building section with the BESTIX Promise
 */

import { Shield, DollarSign, Headphones, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

const values = [
  {
    icon: Shield,
    title: 'Verified Authentic',
    description: 'Every ticket verified through our proprietary authentication system. Your purchase protected by our 100% guarantee.',
  },
  {
    icon: DollarSign,
    title: 'Intelligent Pricing',
    description: 'Our algorithms find you the best value across the market. Price alerts ensure you never overpay.',
  },
  {
    icon: Headphones,
    title: 'White-Glove Concierge',
    description: 'Our team of experts available 24/7 to curate your perfect experience.',
  },
  {
    icon: Zap,
    title: 'Instant Access',
    description: 'Tickets delivered directly to your mobile wallet the moment you purchase.',
  },
];

interface ValuePropositionProps {
  variant?: 'light' | 'dark';
  className?: string;
}

export function ValueProposition({ variant = 'light', className }: ValuePropositionProps) {
  const isDark = variant === 'dark';

  return (
    <section className={cn('py-20 px-6', isDark ? 'bg-[var(--color-navy)]' : 'bg-white', className)}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className={cn('w-16 h-px mx-auto mb-6', isDark ? 'bg-white/40' : 'bg-[var(--color-navy)]')} />
          <h2 className={cn('font-playfair italic text-3xl md:text-4xl mb-2', isDark ? 'text-white' : 'text-[var(--color-navy)]')}>
            The BESTIX Promise
          </h2>
          <p className={cn('text-lg', isDark ? 'text-white/70' : 'text-gray-500')}>
            Every ticket backed by our commitment to you
          </p>
          <div className={cn('w-16 h-px mx-auto mt-6', isDark ? 'bg-white/40' : 'bg-[var(--color-navy)]')} />
        </div>

        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
          {values.map((value) => {
            const Icon = value.icon;
            return (
              <div key={value.title} className="flex gap-5">
                <div className={cn(
                  'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
                  isDark ? 'bg-[var(--color-gold)]/20' : 'bg-[var(--color-navy)]/10'
                )}>
                  <Icon className={cn('w-6 h-6', isDark ? 'text-[var(--color-gold)]' : 'text-[var(--color-navy)]')} />
                </div>
                <div>
                  <h3 className={cn('font-playfair text-xl mb-2', isDark ? 'text-white' : 'text-[var(--color-navy)]')}>
                    {value.title}
                  </h3>
                  <div className={cn('w-10 h-px mb-3', isDark ? 'bg-[var(--color-gold)]/50' : 'bg-[var(--color-navy)]/30')} />
                  <p className={cn('leading-relaxed', isDark ? 'text-white/70' : 'text-gray-600')}>
                    {value.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default ValueProposition;
